import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5d361660"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "header-title"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  key: 0,
  class: "pb-5 mb-5"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "pb-4 pt-3" }, null, -1))
const _hoisted_5 = {
  key: 1,
  class: "order-fetch-failed"
}

import QRCode from "qrcode";
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { officeService } from "@/app/services/OfficeService";
import { orderService } from "@/app/services/OrderService";
import EmbeddedJobPosting from "@/components/atoms/other/EmbeddedJobPosting.vue";
import AppInstallationGuidingModal from "@/components/molecules/other/AppInstallationGuidingModal.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderCard from "@/components/organisms/orders/OrderCardForJobPosting.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import OrderIntroContents from "@/components/organisms/orders/OrderIntroContents.vue";
import OrderTabContents from "@/components/organisms/orders/OrderTabContents.vue";
import { APP_START_URL } from "@/const/app";
import { JobPosting } from "@/models/jobPosting";
import { masterStore } from "@/store/master";
import { OrderDetail } from "@/types/order/Order";
import { isMobileBrowser, openInNewWindow } from "@/utils/browserUtil";
import { makeGoogleMapConfirmRouteUrl } from "@/utils/googlemap";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";


export default /*#__PURE__*/_defineComponent({
  __name: 'DetailForJobPosting',
  setup(__props) {

const loader = useLoader();
const errorModal = useErrorModal();
const route = useRoute();
const router = useRouter();

const isInitializeComplete = ref(false);
const orderDetail = ref<OrderDetail | null>(null);
const isAppInstallationModalVisible = ref(false);
const isOrderFetchingFailed = ref(true);
const selectedWorkTimeOptionId = ref<number | null>(null);
const evaluationTotalAverage = ref<number | null>(null);
const evaluationCount = ref(0);

onBeforeMount(async () => {
  await init(orderId.value);
});

onMounted(() => {
  const canonical = document.createElement("link");
  canonical.rel = "canonical";
  canonical.href = new URL(router.currentRoute.value.path, APP_START_URL).href;
  document.head.appendChild(canonical);
});

onBeforeUnmount(() => {
  // このページからアプリ内の他ページに遷移することは無いはずだが、念のため head 内の状態を戻しておく
  const links = [...document.head.getElementsByTagName("link")];
  const canonical = links.find(s => s.rel === "canonical");
  if (canonical) {
    document.head.removeChild(canonical);
  }
  const robots = links.find(s => s.rel === "robots");
  if (robots) {
    document.head.removeChild(robots);
  }
  document.title = "カイテク";
});

// 重複ページと判断されないよう、ページのタイトルを変更しておく
watch(
  () => orderDetail.value,
  (newOrderDetail: OrderDetail | null) => {
    if (newOrderDetail) {
      document.title = `${newOrderDetail.title} | カイテク`;
      // 公開中でない場合は noindex を付与し、ページが今後インデックスされないようにしておく
      if (!newOrderDetail.isHiring) {
        addNoindex();
      }
    }
  }
);
watch(
  () => isOrderFetchingFailed.value,
  (newValue: boolean) => {
    // 停止中になったなど、仕事情報が閲覧できなくなったケース
    // この場合は noindex を付与し、ページが今後インデックスされないようにしておく
    if (newValue) {
      addNoindex();
    }
  }
);

const orderId = computed(() => route.params.orderId as string);
const selectedWorkTimeOption = computed(() => {
  if (!orderDetail.value || selectedWorkTimeOptionId.value === null) {
    return null;
  }

  return (
    orderDetail.value.workTimeOptions.find(
      option => option.id === selectedWorkTimeOptionId.value
    ) || null
  );
});
const orderServiceName = computed(() => {
  if (!orderDetail.value) {
    return "";
  }

  return masterStore.getServiceNameById(orderDetail.value.service.id) || "";
});
const headerTitle = computed(() => {
  if (!orderDetail.value) {
    return "";
  }

  return `${orderServiceName.value}のお仕事`;
});
const jobPostingBody = computed(() => {
  if (!orderDetail.value) {
    return undefined;
  }

  return new JobPosting(orderDetail.value);
});

async function init(orderId: string) {
  try {
    loader.show();
    try {
      isInitializeComplete.value = false;
      isOrderFetchingFailed.value = false;
      const order = await orderService.fetchOrder(Number(orderId), false);
      // このページは Google に掲載した求人情報のリンクから表示されることを目的としている
      // 募集終了の場合は構造化データが出力されず、リンクもされないためエラーにしておく
      if (!order.isHiring) {
        isOrderFetchingFailed.value = true;
        return;
      }
      orderDetail.value = order;
      // 勤務時間については1件目を選択しておく
      // 0件のケースはありえないはず
      if (orderDetail.value.workTimeOptions.length > 0) {
        selectedWorkTimeOptionId.value =
          orderDetail.value.workTimeOptions[0].id;
      }
      // 事業所の平均評価については非同期で取得する
      fetchOfficeEvaluation(orderDetail.value);
    } catch (error) {
      isOrderFetchingFailed.value = true;
      return;
    }
  } catch (error) {
    await errorModal.showErrorModal(error as Error);
  } finally {
    loader.hide();
  }
  isInitializeComplete.value = true;
}
async function fetchOfficeEvaluation(orderDetail: OrderDetail) {
  const evaluation = await officeService.fetchTotalAvgEvaluationsByWorker(
    orderDetail.office.id
  );
  evaluationCount.value = evaluation.count;
  evaluationTotalAverage.value = evaluation.totalAverage;
}
function guideInstallation() {
  if (isMobileBrowser()) {
    openInNewWindow(appInstallationPortalURL());
    return;
  }

  showAppInstallationModal();
}
function showAppInstallationModal() {
  isAppInstallationModalVisible.value = true;
}

function closeAppInstallationModal() {
  isAppInstallationModalVisible.value = false;
}

function appInstallationPortalURL(): string {
  return process.env.VUE_APP_URL_APP_INSTALLATION_PORTAL!;
}
function onClickViewRoute() {
  /**
   * HACK: 本来はcordova-plugin-white-listで制限しているため自動で外部ブラウザに移動するが、googleMapのiframeの読み込みが必要なため、
   * googleMapのルートに移動する時だけプラグインを利用する
   *
   */
  openInNewWindow(
    makeGoogleMapConfirmRouteUrl(
      orderDetail.value!.office.latitude,
      orderDetail.value!.office.longitude
    )
  );
}
async function onClickNoticeOfEmployment() {
  try {
    loader.show();
    const url = await getPdfUrl();
    if (url) {
      openInNewWindow(url);
    }
  } catch (error) {
    await errorModal.showErrorModal(error as Error);
  } finally {
    loader.hide();
  }
}
async function getPdfUrl() {
  const res = await orderService.getNotFixedNoticeOfEmploymentPdfUrl(
    orderDetail.value!.id,
    selectedWorkTimeOption.value!.id
  );
  return res.url;
}
function onClickAttachmentFile() {
  if (!orderDetail.value || !orderDetail.value.attachmentFile) {
    return;
  }
  openInNewWindow(orderDetail.value.attachmentFile);
}
function addNoindex() {
  const links = [...document.head.getElementsByTagName("link")];
  const robots = links.find(s => s.rel === "robots");
  // すでに追加されていた場合は何もしない
  if (robots) {
    return;
  }
  const meta = document.createElement("meta");
  meta.name = "robots";
  meta.content = "noindex, nofollow";
  document.head.appendChild(meta);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createVNode(Header, null, {
        default: _withCtx(() => [
          (isInitializeComplete.value && orderDetail.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(headerTitle.value), 1))
            : _createCommentVNode("", true),
          _createTextVNode(),
          (isOrderFetchingFailed.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "エラー"))
            : _createCommentVNode("", true)
        ], undefined, true),
        _: 1
      }),
      _createTextVNode(),
      (isInitializeComplete.value && orderDetail.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _hoisted_4,
            _createTextVNode(),
            _createVNode(OrderCard, {
              order: orderDetail.value,
              orderServiceName: orderServiceName.value
            }, null, 8, ["order", "orderServiceName"]),
            _createTextVNode(),
            _createVNode(OrderIntroContents, {
              orderDetail: orderDetail.value,
              class: "mt-2"
            }, null, 8, ["orderDetail"]),
            _createTextVNode(),
            _createVNode(OrderTabContents, {
              order: orderDetail.value,
              isLogin: false,
              evaluationCount: evaluationCount.value,
              evaluationTotalAverage: evaluationTotalAverage.value,
              forceExpandDescriptionDisabled: true,
              class: "mt-2",
              onClickNoticeOfEmployment: onClickNoticeOfEmployment,
              onClickAttachmentFile: onClickAttachmentFile,
              onClickViewRoot: onClickViewRoute
            }, null, 8, ["order", "evaluationCount", "evaluationTotalAverage"]),
            _createTextVNode(),
            _createVNode(OrderFixedButton, { onClick: guideInstallation }, {
              default: _withCtx(() => [
                _createTextVNode("\n        アプリをインストールして応募する\n      ")
              ], undefined, true),
              _: 1
            })
          ]))
        : _createCommentVNode("", true),
      _createTextVNode(),
      (isOrderFetchingFailed.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, "\n      お仕事の取得に失敗しました\n    "))
        : _createCommentVNode("", true),
      _createTextVNode(),
      _createVNode(AppInstallationGuidingModal, {
        "is-visible": isAppInstallationModalVisible.value,
        "close-modal": closeAppInstallationModal,
        appInstallationPortalURL: appInstallationPortalURL,
        "qr-code-lib": _unref(QRCode)
      }, null, 8, ["is-visible", "qr-code-lib"])
    ]),
    _createTextVNode(),
    (!isOrderFetchingFailed.value)
      ? (_openBlock(), _createBlock(EmbeddedJobPosting, {
          key: 0,
          body: jobPostingBody.value
        }, null, 8, ["body"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})